import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useShop } from '@/hooks/useShop';
import { shopCategories } from '@/lib/brand';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Confiscation {
  id: string;
  loan_id: string | null;
  customer_name: string;
  phone: string | null;
  item_name: string;
  item_type: string;
  seize_date: string;
  seize_reason: string;
  estimated_value: number;
  selling_price: number | null;
  selling_date: string | null;
  status: string;
  notes: string | null;
  created_at: string | null;
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: 'Chờ xử lý', className: 'bg-amber-100 text-amber-700' },
  sold: { label: 'Đã bán', className: 'bg-emerald-100 text-emerald-700' },
  disposed: { label: 'Đã thanh lý', className: 'bg-gray-100 text-gray-600' },
};

const FALLBACK_TYPE_OPTIONS = ['Xe máy', 'Điện thoại', 'Laptop', 'Trang sức', 'Xe đạp điện', 'Máy tính bảng', 'Khác'];

const LOAN_TO_CONFISCATION_TYPE: Record<string, string> = {
  'Vàng bạc': 'Trang sức',
  'Trang sức': 'Trang sức',
  'Điện tử': 'Điện thoại',
  'Điện thoại': 'Điện thoại',
  'Laptop': 'Laptop',
  'Xe máy': 'Xe máy',
  'Ô tô': 'Xe máy',
};

function formatMoney(value: number | null): string {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('vi-VN');
}

function parseMoney(raw: string): number {
  const cleaned = raw.replace(/\./g, '').replace(/[^0-9]/g, '');
  return cleaned ? Number(cleaned) : 0;
}

export default function ConfiscationsPage() {
  const { user } = useAuth();
  const { shop } = useShop();
  const typeOptions = shopCategories(shop).length ? shopCategories(shop) : FALLBACK_TYPE_OPTIONS;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const newId = searchParams.get('new');
  const [items, setItems] = useState<Confiscation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Confiscation | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Confiscation | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);

  // Auto-hide toast after 3s
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const [form, setForm] = useState({
    customer_name: '',
    phone: '',
    item_name: '',
    item_type: 'Xe máy',
    seize_date: new Date().toISOString().split('T')[0],
    seize_reason: 'Quá hạn thanh toán',
    estimated_value: '',
    selling_price: '',
    selling_date: '',
    status: 'pending',
    notes: '',
    loan_id: null as string | null,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  // Sau khi fetch items, nếu có ?new= thì tự mở edit modal
  useEffect(() => {
    if (!newId || items.length === 0) return;
    const found = items.find(i => i.loan_id === newId || i.id === newId);
    if (found) {
      setTimeout(() => {
        setEditing(found);
        setForm({
          customer_name: found.customer_name,
          phone: found.phone ?? '',
          item_name: found.item_name,
          item_type: found.item_type,
          seize_date: found.seize_date,
          seize_reason: found.seize_reason,
          estimated_value: formatMoney(found.estimated_value),
          selling_price: formatMoney(found.selling_price),
          selling_date: found.selling_date ?? '',
          status: found.status,
          notes: found.notes ?? '',
          loan_id: found.loan_id,
        });
        setModalOpen(true);
      }, 300);
    }
  }, [newId, items]);

  async function fetchItems() {
    setLoading(true);
    try {
      // 1. Lấy các khoản vay bị tịch thu (status = 'forfeited')
      const { data: loansData, error: loansError } = await supabase
        .from('loans')
        .select('*')
        .eq('status', 'forfeited')
        .order('due_date', { ascending: false });

      if (loansError) throw loansError;

      // 2. Lấy confiscations đã tồn tại để merge thông tin bán hàng
      const loanIds = (loansData || []).map((l: Record<string, unknown>) => l.id as string);
      const confMap: Record<string, Record<string, unknown>> = {};
      if (loanIds.length > 0) {
        const { data: confData } = await supabase
          .from('confiscations')
          .select('*')
          .in('loan_id', loanIds);
        (confData || []).forEach((c: Record<string, unknown>) => {
          confMap[c.loan_id as string] = c;
        });
      }

      // 3. Merge thành Confiscation items
      const merged: Confiscation[] = (loansData || []).map((loan: Record<string, unknown>) => {
        const conf = confMap[loan.id as string];
        return {
          id: (conf?.id as string) || `TMP-${loan.id}`,
          loan_id: loan.id as string,
          customer_name: (loan.customer_name as string) || '',
          phone: (loan.customer_phone as string) || null,
          item_name: (loan.asset_name as string) || '',
          item_type: LOAN_TO_CONFISCATION_TYPE[loan.asset_category as string] || (loan.asset_category as string) || 'Khác',
          seize_date: (loan.due_date as string) || new Date().toISOString().split('T')[0],
          seize_reason: (conf?.seize_reason as string) || 'Quá hạn thanh toán',
          estimated_value: Number(loan.loan_amount || 0),
          selling_price: (conf?.selling_price as number | null) ?? null,
          selling_date: (conf?.selling_date as string | null) ?? null,
          status: (conf?.status as string) || 'pending',
          notes: (conf?.notes as string) || `Tự động từ khoản vay ${loan.id}`,
          created_at: (conf?.created_at as string) || (loan.due_date as string),
        };
      });

      setItems(merged);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }

  function openAdd() {
    setEditing(null);
    setForm({
      customer_name: '',
      phone: '',
      item_name: '',
      item_type: 'Xe máy',
      seize_date: new Date().toISOString().split('T')[0],
      seize_reason: 'Quá hạn thanh toán',
      estimated_value: '',
      selling_price: '',
      selling_date: '',
      status: 'pending',
      notes: '',
      loan_id: null,
    });
    setModalOpen(true);
  }

  function openEdit(item: Confiscation) {
    setEditing(item);
    setForm({
      customer_name: item.customer_name,
      phone: item.phone ?? '',
      item_name: item.item_name,
      item_type: item.item_type,
      seize_date: item.seize_date,
      seize_reason: item.seize_reason,
      estimated_value: formatMoney(item.estimated_value),
      selling_price: formatMoney(item.selling_price),
      selling_date: item.selling_date ?? '',
      status: item.status,
      notes: item.notes ?? '',
      loan_id: item.loan_id,
    });
    setModalOpen(true);
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) {
    const { name, value } = e.target;
    if (name === 'estimated_value' || name === 'selling_price') {
      const num = parseMoney(value);
      setForm((f) => ({ ...f, [name]: num ? num.toLocaleString('vi-VN') : '' }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      customer_name: form.customer_name.trim(),
      phone: form.phone.trim() || null,
      item_name: form.item_name.trim(),
      item_type: form.item_type,
      seize_date: form.seize_date,
      seize_reason: form.seize_reason.trim(),
      estimated_value: parseMoney(form.estimated_value),
      selling_price: form.selling_price ? parseMoney(form.selling_price) : null,
      selling_date: form.selling_date || null,
      status: form.status,
      notes: form.notes.trim() || null,
      loan_id: form.loan_id || null,
    };

    try {
      const isFromLoan = editing && editing.id.startsWith('TMP-');
      if (editing && !isFromLoan) {
        // Update existing confiscation record (id is UUID from DB)
        const { error } = await supabase.from('confiscations').update(payload).eq('id', editing.id);
        if (error) throw error;
        setToast({ message: 'Cập nhật tịch thu thành công!', type: 'success' });
      } else {
        // Insert new — để DB tự generate UUID, không tự set id
        const { data: inserted, error } = await supabase
          .from('confiscations')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        // Cập nhật local item với UUID thật từ DB
        if (inserted && editing) {
          setItems((prev) =>
            prev.map((i) =>
              i.id === editing.id ? { ...i, id: inserted.id, ...payload, created_at: new Date().toISOString() } : i
            )
          );
        }
        setToast({ message: 'Thêm tịch thu thành công!', type: 'success' });
      }
      setModalOpen(false);
      await fetchItems();
    } catch {
      if (editing && !editing.id.startsWith('TMP-')) {
        // Fallback: update local state
        setItems((prev) =>
          prev.map((i) =>
            i.id === editing.id
              ? { ...i, ...payload, id: i.id, created_at: i.created_at, loan_id: i.loan_id }
              : i
          )
        );
        setToast({ message: 'Cập nhật tịch thu (offline)', type: 'warning' });
      } else {
        // Fallback cho insert
        const fallbackId = `TMP-${Date.now()}`;
        const newItem: Confiscation = {
          id: fallbackId,
          loan_id: payload.loan_id,
          ...payload,
          created_at: new Date().toISOString(),
        };
        setItems((prev) => [newItem, ...prev]);
        setToast({ message: 'Thêm tịch thu (offline) — vui lòng thử lại!', type: 'error' });
      }
      setModalOpen(false);
    }
    setSaving(false);
  }

  async function handleDelete(item: Confiscation) {
    // Nếu là TMP (chưa có trong confiscations), chỉ remove khỏi list
    if (item.id.startsWith('TMP-')) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setToast({ message: 'Đã xóa khỏi danh sách tịch thu!', type: 'success' });
    } else {
      try {
        const { error } = await supabase.from('confiscations').delete().eq('id', item.id);
        if (error) throw error;
        setToast({ message: 'Đã xóa!', type: 'success' });
      } catch {
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        setToast({ message: 'Đã xóa (offline)', type: 'warning' });
      }
    }
    setDeleteConfirm(null);
  }

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return items.filter((i) => {
      const matchSearch =
        !s ||
        i.customer_name.toLowerCase().includes(s) ||
        i.item_name.toLowerCase().includes(s) ||
        i.item_type.toLowerCase().includes(s) ||
        i.phone?.toLowerCase().includes(s);
      const matchStatus = statusFilter === 'all' || i.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [items, search, statusFilter]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50">
            <i className="ri-alarm-warning-line text-lg text-red-600"></i>
          </span>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Quản Lý Tịch Thu</h1>
            <p className="text-xs md:text-sm text-gray-500">Tài sản bị tịch thu từ khoản vay quá hạn</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Thêm tịch thu
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            placeholder="Tìm theo tên khách, tài sản, loại..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white"
        >
          <option value="all">Tất cả trạng thái</option>
          <option value="pending">Chờ xử lý</option>
          <option value="sold">Đã bán</option>
          <option value="disposed">Đã thanh lý</option>
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-sm text-gray-500">Đang tải...</span>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && filtered.length > 0 && (
        <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Khách hàng</th>
                <th className="px-4 py-3 text-left font-semibold">Tài sản</th>
                <th className="px-4 py-3 text-left font-semibold">Ngày tịch thu</th>
                <th className="px-4 py-3 text-right font-semibold">Giá ước tính</th>
                <th className="px-4 py-3 text-center font-semibold">Trạng thái</th>
                <th className="px-4 py-3 text-right font-semibold">Giá bán</th>
                <th className="px-4 py-3 text-center font-semibold">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => {
                const st = STATUS_LABELS[item.status] || { label: item.status, className: 'bg-gray-100 text-gray-600' };
                return (
                  <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.customer_name}</div>
                      <div className="text-xs text-gray-500">{item.phone || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.item_name}</div>
                      <div className="text-xs text-gray-500">{item.item_type}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {new Date(item.seize_date).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {formatMoney(item.estimated_value)}đ
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {item.selling_price ? (
                        <span className="font-medium text-emerald-700">{formatMoney(item.selling_price)}đ</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-500"
                          title="Sửa"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(item)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-500"
                          title="Xóa"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Cards */}
      {!loading && filtered.length > 0 && (
        <div className="md:hidden space-y-3">
          {filtered.map((item) => {
            const st = STATUS_LABELS[item.status] || { label: item.status, className: 'bg-gray-100 text-gray-600' };
            return (
              <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.item_name}</h3>
                    <p className="text-xs text-gray-500">{item.item_type}</p>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.className}`}>
                    {st.label}
                  </span>
                </div>
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Khách:</span> {item.customer_name} · {item.phone || '-'}
                </div>
                <div className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Ngày tịch thu:</span>{' '}
                  {new Date(item.seize_date).toLocaleDateString('vi-VN')}
                </div>
                <div className="flex items-center gap-4 text-sm mb-3">
                  <div>
                    <span className="font-medium">Ước tính:</span>{' '}
                    <span className="font-semibold text-gray-900">{formatMoney(item.estimated_value)}đ</span>
                  </div>
                  {item.selling_price && (
                    <div>
                      <span className="font-medium">Bán:</span>{' '}
                      <span className="font-semibold text-emerald-700">{formatMoney(item.selling_price)}đ</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-pencil-line text-xs"></i>
                    Sửa
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                    Xóa
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-alarm-warning-line text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Không có tịch thu nào</h3>
          <p className="text-sm text-gray-500 mb-3">Chưa có khoản vay nào bị tịch thu. Đánh dấu "Tịch thu" trong Quản lý Khoản Vay để hiển thị ở đây.</p>
          <button
            onClick={() => navigate('/loans')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-medium hover:bg-[#d97706] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Về Quản lý Khoản Vay
          </button>
        </div>
      )}

      {/* Modal Add/Edit */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'Sửa tịch thu' : 'Thêm tịch thu mới'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-500"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tên khách hàng *</label>
                  <input
                    name="customer_name"
                    value={form.customer_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    placeholder="Tên khách hàng"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Số điện thoại</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    placeholder="Số điện thoại"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tên tài sản *</label>
                  <input
                    name="item_name"
                    value={form.item_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    placeholder="Tên tài sản"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Loại tài sản *</label>
                  <select
                    name="item_type"
                    value={form.item_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white"
                  >
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Ngày tịch thu *</label>
                  <input
                    type="date"
                    name="seize_date"
                    value={form.seize_date}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Trạng thái</label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-white"
                  >
                    <option value="pending">Chờ xử lý</option>
                    <option value="sold">Đã bán</option>
                    <option value="disposed">Đã thanh lý</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Lý do tịch thu *</label>
                <input
                  name="seize_reason"
                  value={form.seize_reason}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  placeholder="Quá hạn thanh toán"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Giá ước tính (VNĐ) *</label>
                  <input
                    name="estimated_value"
                    value={form.estimated_value}
                    onChange={handleChange}
                    required
                    inputMode="numeric"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    placeholder="18.000.000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Giá bán ra (VNĐ)</label>
                  <input
                    name="selling_price"
                    value={form.selling_price}
                    onChange={handleChange}
                    inputMode="numeric"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    placeholder="15.500.000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ngày bán</label>
                <input
                  type="date"
                  name="selling_date"
                  value={form.selling_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ghi chú</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                  placeholder="Thông tin thêm..."
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {saving ? 'Đang lưu...' : editing ? 'Lưu thay đổi' : 'Thêm tịch thu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm p-5 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Xác nhận xóa?</h3>
            <p className="text-sm text-gray-500 mb-4">
              Bạn có chắc muốn xóa tịch thu của <strong>{deleteConfirm.customer_name}</strong> -{' '}
              <strong>{deleteConfirm.item_name}</strong>?
            </p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Hủy
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] animate-in slide-in-from-bottom-2 fade-in duration-200">
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : toast.type === 'error'
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            <i
              className={`${
                toast.type === 'success'
                  ? 'ri-check-line'
                  : toast.type === 'error'
                  ? 'ri-error-warning-line'
                  : 'ri-alert-line'
              } text-lg`}
            ></i>
            <span>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 w-5 h-5 flex items-center justify-center rounded hover:bg-black/5 cursor-pointer"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}